import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
  Method,
} from 'axios';

import { addSentryApiBreadcrumb, captureSentryApiFailure } from '@/util/sentry';

import {
  type ApiStatusResponseBody,
  getApiStatusCode,
  isApiStatusResponseBody,
  isNon2xxStatus,
} from './apiStatus';

type SentryAxiosRequestConfig = (
  | AxiosRequestConfig
  | InternalAxiosRequestConfig
) & {
  _sentryStartTime?: number;
};

type SentryAxiosError = {
  config?: SentryAxiosRequestConfig;
  data?: unknown;
  response?: {
    data?: unknown;
    status?: Nullish<number>;
  };
  status?: Nullish<number>;
};

const UNAUTHORIZED_HTTP_STATUS = 401;

const getDuration = (config: Nullish<SentryAxiosRequestConfig>) =>
  Date.now() - (config?._sentryStartTime ?? Date.now());

const getResultCode = (data: unknown) =>
  isApiStatusResponseBody(data) ? data.resultCode : null;

const getTrID = (data: unknown) =>
  isApiStatusResponseBody(data) ? data.trID : null;

const getFailureSource = (
  statusCode: Nullish<number>,
): 'api-response-error' | 'api-response-status-code' =>
  isNon2xxStatus(statusCode)
    ? 'api-response-status-code'
    : 'api-response-error';

const addApiBreadcrumb = ({
  data,
  duration,
  method,
  status,
  statusCode,
  url,
}: {
  data: unknown;
  duration: number;
  method?: Nullish<Method | string>;
  status?: Nullish<number>;
  statusCode?: Nullish<number>;
  url?: Nullish<string>;
}) => {
  addSentryApiBreadcrumb({
    duration,
    method,
    resultCode: getResultCode(data),
    status,
    statusCode,
    trID: getTrID(data),
    url,
  });
};

const captureApiFailure = ({
  data,
  duration,
  method,
  status,
  statusCode,
  url,
}: {
  data: unknown;
  duration: number;
  method?: Nullish<Method | string>;
  status?: Nullish<number>;
  statusCode?: Nullish<number>;
  url?: Nullish<string>;
}) => {
  if (!isNon2xxStatus(statusCode) && !isNon2xxStatus(status)) return;

  // 401 인증 오류는 애플리케이션 버그가 아니라 에러 분석에 혼선을 주므로 Sentry 수집에서 제외합니다.
  if (status === UNAUTHORIZED_HTTP_STATUS) return;

  captureSentryApiFailure({
    duration,
    method,
    resultCode: getResultCode(data),
    source: getFailureSource(statusCode),
    status,
    statusCode,
    trID: getTrID(data),
    url,
  });
};

const handleApiResponse = (response: AxiosResponse<ApiStatusResponseBody>) => {
  const config = response.config as SentryAxiosRequestConfig;
  const duration = getDuration(config);
  const statusCode = getApiStatusCode(response.data);

  addApiBreadcrumb({
    data: response.data,
    duration,
    method: config.method,
    status: response.status,
    statusCode,
    url: config.url,
  });
  captureApiFailure({
    data: response.data,
    duration,
    method: config.method,
    status: response.status,
    statusCode,
    url: config.url,
  });
};

const handleApiResponseError = (error: SentryAxiosError) => {
  const config = error.config;
  const duration = getDuration(config);
  const data = error.response?.data ?? error.data;
  const status = error.response?.status ?? error.status ?? null;
  const statusCode = getApiStatusCode(data);

  addApiBreadcrumb({
    data,
    duration,
    method: config?.method,
    status,
    statusCode,
    url: config?.url,
  });
  captureApiFailure({
    data,
    duration,
    method: config?.method,
    status,
    statusCode,
    url: config?.url,
  });
};

export const registerSentryAxiosInterceptor = (instance: AxiosInstance) => {
  instance.interceptors.request.use(config => {
    (config as SentryAxiosRequestConfig)._sentryStartTime = Date.now();

    return config;
  });

  instance.interceptors.response.use(
    response => {
      handleApiResponse(response);

      return response;
    },
    error => {
      handleApiResponseError(error);

      return Promise.reject(error);
    },
  );
};
